import React, { useState, useEffect } from "react";
import {
  FormGroup,
  InputGroup,
  Spinner,
  TextArea,
  Intent,
  Button,
  Callout
} from "@blueprintjs/core";
import styles from "./ProfileSettings.module.scss";
import { ImageUploadArea } from "../../../molecules/ImageUploadArea";
import { PasswordInput, EmailInput } from ".";
import { useChangeProfile } from "./hook";
import { useQuery } from "@apollo/react-hooks";
import { GetProfile } from "../queries";
import { Toolbar, ToolbarGroup } from "../../../molecules/Toolbar";
import SettingsTab from "../SettingsTab";
import SettingsForm from "../SettingsForm";
import FormInfo from "../../../atoms/FormInfo/FormInfo";
import FormSection from "../../../atoms/FormSection/FormSection";

function ProfileSettings() {
  const { data, loading } = useQuery(GetProfile);
  const profile = !loading ? data.accounts[0] : undefined;

  const {
    error,
    name,
    email,
    bio,
    supportEmail,
    termsOfServiceUrl,
    refundPolicyUrl,
    privacyPolicyUrl,
    website,
    image,
    statementDescriptor,
    onImageChange,
    setName,
    setEmail,
    setBio,
    setSupportEmail,
    setTermsOfServiceUrl,
    setPrivacyPolicyUrl,
    setRefundPolicyUrl,
    setWebsite,
    setStatementDescriptor,
    errorField,
    isSaving,
    saveChanges
  } = useChangeProfile({ profile });

  if (loading || !data) {
    return (
      <SettingsTab centered>
        <Spinner />
      </SettingsTab>
    );
  }

  return (
    <SettingsTab>
      <Toolbar title="Settings">
        <ToolbarGroup>
          <Button
            text="Save"
            intent={Intent.PRIMARY}
            loading={isSaving}
            disabled={isSaving}
            onClick={saveChanges}
          />
        </ToolbarGroup>
      </Toolbar>

      <SettingsForm>
        {error && (
          <Callout
            icon="error"
            intent={Intent.DANGER}
            title="We couldn't save your settings."
          >
            {error.message}
          </Callout>
        )}

        <div className={styles.avatar}>
          <ImageUploadArea image={image} onImageChange={onImageChange} />
        </div>

        <FormSection>
          <FormGroup label="Name">
            <InputGroup
              type="text"
              autoCapitalize="none"
              autoCorrect="false"
              value={name}
              placeholder="Enter Name"
              onChange={setName}
              intent={errorField === "name" ? Intent.DANGER : Intent.NONE}
            />
          </FormGroup>
          <FormGroup label="Bio">
            <TextArea
              type="text"
              autoCapitalize="none"
              autoCorrect="false"
              value={bio}
              placeholder="Enter your bio"
              onChange={setBio}
              intent={errorField === "bio" ? Intent.DANGER : Intent.NONE}
              fill
            />
          </FormGroup>
          <FormGroup label="Website">
            <InputGroup
              type="text"
              autoCapitalize="none"
              placeholder="Enter Website URL"
              autoCorrect="false"
              value={website}
              intent={errorField === "website" ? Intent.DANGER : Intent.NONE}
              onChange={setWebsite}
            />
          </FormGroup>

          <EmailInput email={email} onUpdate={setEmail} />
        </FormSection>
        {profile && !profile.isNmiAccount && (
          <FormSection>
            <FormGroup label="Charge on Statement">
              <FormInfo>
                Please enter the name you would like to show on the credit card
                statement.
              </FormInfo>
              <FormGroup>
                <InputGroup
                  type="text"
                  placeholder="Enter Statement Descriptor"
                  autoCapitalize="none"
                  autoCorrect="false"
                  value={statementDescriptor}
                  intent={
                    errorField === "statementDescriptor"
                      ? Intent.DANGER
                      : Intent.NONE
                  }
                  onChange={setStatementDescriptor}
                />
              </FormGroup>
            </FormGroup>
          </FormSection>
        )}

        <FormSection>
          <PasswordInput profile={profile} />
        </FormSection>

        <FormSection>
          <FormGroup label="Customer Support">
            <FormInfo>
              Please provide your customer support email and links to your
              brand’s policies. This information will be shown at checkout, on
              the receipts that the customer receives, and in your profile.
            </FormInfo>

            <FormGroup label="Support Email">
              <InputGroup
                type="email"
                placeholder="Enter Support Email"
                autoCapitalize="none"
                autoCorrect="false"
                value={supportEmail}
                intent={
                  errorField === "supportEmail" ? Intent.DANGER : Intent.NONE
                }
                onChange={setSupportEmail}
              />
            </FormGroup>
            <FormGroup label="Terms & Conditions">
              <InputGroup
                type="url"
                autoCapitalize="none"
                autoCorrect="false"
                value={termsOfServiceUrl}
                placeholder="Enter Terms & Conditions URL"
                intent={
                  errorField === "termsOfServiceUrl"
                    ? Intent.DANGER
                    : Intent.NONE
                }
                onChange={setTermsOfServiceUrl}
              />
            </FormGroup>
            <FormGroup label="Privacy Policy">
              <InputGroup
                type="url"
                autoCapitalize="none"
                autoCorrect="false"
                placeholder="Enter Privacy Policy URL"
                value={privacyPolicyUrl}
                intent={
                  errorField === "privacyPolicyUrl"
                    ? Intent.DANGER
                    : Intent.NONE
                }
                onChange={setPrivacyPolicyUrl}
              />
            </FormGroup>
            <FormGroup label="Refund Policy">
              <InputGroup
                type="url"
                placeholder="Enter Refund Policy URL"
                autoCapitalize="none"
                autoCorrect="false"
                value={refundPolicyUrl}
                intent={
                  errorField === "refundPolicyUrl" ? Intent.DANGER : Intent.NONE
                }
                onChange={setRefundPolicyUrl}
              />
            </FormGroup>
          </FormGroup>
        </FormSection>
      </SettingsForm>
    </SettingsTab>
  );
}

export default ProfileSettings;
